import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import logisticsService from '../../services/logisticsService';
import toast from 'react-hot-toast';

export const fetchFreightRequests = createAsyncThunk(
  'logistics/fetchFreightRequests',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await logisticsService.getFreightRequests(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch freight requests');
    }
  }
);

export const createFreightRequest = createAsyncThunk(
  'logistics/createFreightRequest',
  async (freightData, { rejectWithValue }) => {
    try {
      const { data } = await logisticsService.createFreightRequest(freightData);
      toast.success('Freight request created');
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to create freight request');
    }
  }
);

export const fetchShipments = createAsyncThunk(
  'logistics/fetchShipments',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await logisticsService.getShipments(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch shipments');
    }
  }
);

export const fetchShipment = createAsyncThunk(
  'logistics/fetchShipment',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await logisticsService.getShipment(id);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch shipment');
    }
  }
);

export const fetchEarnings = createAsyncThunk(
  'logistics/fetchEarnings',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await logisticsService.getEarnings(params);
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to fetch earnings');
    }
  }
);

const initialState = {
  freightRequests: [],
  freightTotalCount: 0,
  freightLoading: false,
  shipments: [],
  currentShipment: null,
  shipmentTotalCount: 0,
  shipmentLoading: false,
  earnings: [],
  earningsSummary: null,
  earningsLoading: false,
  error: null,
};

const logisticsSlice = createSlice({
  name: 'logistics',
  initialState,
  reducers: {
    clearCurrentShipment: (state) => { state.currentShipment = null; },
    clearError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFreightRequests.pending, (state) => { state.freightLoading = true; })
      .addCase(fetchFreightRequests.fulfilled, (state, action) => {
        state.freightLoading = false;
        state.freightRequests = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.freightTotalCount = action.payload.count || 0;
      })
      .addCase(fetchFreightRequests.rejected, (state) => { state.freightLoading = false; })
      .addCase(createFreightRequest.fulfilled, (state, action) => {
        state.freightRequests.unshift(action.payload);
      })
      .addCase(fetchShipments.pending, (state) => { state.shipmentLoading = true; })
      .addCase(fetchShipments.fulfilled, (state, action) => {
        state.shipmentLoading = false;
        state.shipments = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.shipmentTotalCount = action.payload.count || 0;
      })
      .addCase(fetchShipments.rejected, (state) => { state.shipmentLoading = false; })
      .addCase(fetchShipment.fulfilled, (state, action) => {
        state.currentShipment = action.payload;
      })
      .addCase(fetchEarnings.pending, (state) => { state.earningsLoading = true; })
      .addCase(fetchEarnings.fulfilled, (state, action) => {
        state.earningsLoading = false;
        state.earnings = (() => { const _d = action.payload; return Array.isArray(_d?.results) ? _d.results : Array.isArray(_d?.items) ? _d.items : Array.isArray(_d) ? _d : []; })();
        state.earningsSummary = action.payload.summary || null;
      })
      .addCase(fetchEarnings.rejected, (state) => { state.earningsLoading = false; });
  },
});

export const { clearCurrentShipment, clearError } = logisticsSlice.actions;
export default logisticsSlice.reducer;
